// The one place a wire shape is read (SPEC-redesign §4.3). S1–S4 see `Item`s and
// `SourceHealth`, never a body from `/api/*`.
import type { Alert } from '../shared/alerts.ts';
import type { Event } from '../shared/events.ts';
import { delayLabel, delayMinutes, LIVE_S } from '../shared/format.ts';
import type { Item, ItemKind, Voice } from '../shared/model.ts';
import { healthOf, sourceHealth, startFeed, type HealthAnswer, type SourceHealth } from './feed.ts';
import { startVehicles, vehiclePositions, type Vehicle } from './vehicles.ts';

export type Snapshot = {
  /** Everything the axis and the circle can show, unfiltered and unsorted. */
  items: Item[];
  /** Per voice. `ztm` is the vehicle feed, `ztmk` the notices. */
  health: Record<Voice, SourceHealth>;
  /**
   * OUR server's last successful refresh of the vehicle snapshot; 0 when
   * nothing has ever loaded. Not how old the data is — that is `ztmFeedTs`.
   */
  ztmUpdatedAt: number;
  /**
   * ZTM's OWN clock: the GTFS-RT header stamp the snapshot was built from, in
   * ms. `null` when the wire gives nothing, or gives a stamp we do not believe.
   * Required rather than optional on purpose — without it a consumer has to
   * decide what to show, not forget the case exists.
   */
  ztmFeedTs: number | null;
  /**
   * False once we cannot reach OUR server — two consecutive polls missed by
   * either signal that knows: `/api/vehicles` (SPEC-foundation §4) or
   * `/api/health`. It outranks every source's own state, because with no answer
   * from our server nothing we hold is news about a source (§4.3).
   */
  serverReachable: boolean;
};

/** A position fix older than this is a vehicle we are no longer sure we can place. */
const STALE_FIX_S = LIVE_S;

/**
 * Past this, a "delay" is feed garbage rather than a late tram, and we say we
 * do not know instead of drawing „+346 min". Observed on the live feed at
 * 03:30 on 2026-09-19: three of thirty-eight vehicles reporting 18 659–20 816 s
 * on night lines 201, 227 and 214, which the axis then sorted to the very top
 * as the most delayed things in the city. Ninety minutes is well past any real
 * delay ZTM publishes and well short of the six hours those three showed.
 * `docs/kb/sources/ztm-poznan.md` records the observation.
 */
const IMPLAUSIBLE_DELAY_S = 5400;

/**
 * How often `/api/health` is asked what the vehicle feed's state is. Slower than
 * the vehicle poll on purpose: the state changes in minutes, the positions in
 * seconds, and this is our own server rather than a source we are polite to.
 */
const HEALTH_POLL_MS = 30_000;

/**
 * §4.4's scale, in full. 1 is full strength; below it the row is dimmed because
 * it is less about now than the rows around it, not because it matters less.
 */
const WEIGHT = { full: 1, started: 0.75, stale: 0.7, early: 0.6, old: 0.55 } as const;
/** Aquanet never says when a fault ends, so after two days we only dim it. */
const OLD_MS = 48 * 60 * 60 * 1000;

/**
 * Past this, a GTFS-RT header stamp is feed garbage rather than an old feed —
 * the same judgement `IMPLAUSIBLE_DELAY_S` makes about a „+346 min" tram. A day
 * is well beyond any outage the kreska still draws a clock for, and a feed that
 * really has not moved in a day is a source that stopped answering, which
 * `health.ztm` is the field for.
 */
const IMPLAUSIBLE_FEED_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * A stamp this far ahead of the browser's clock is still believed: between the
 * two of them the browser's is the one nobody administers, and a phone two
 * minutes fast must not turn a live feed into „no clock at all". Further ahead
 * than this is the feed being wrong, not a watch being wrong.
 */
const CLOCK_SKEW_MS = 2 * 60_000;

/**
 * ZTM's own clock for a vehicle snapshot, in ms, or `null` when there is none
 * to trust. The wire carries GTFS-RT seconds and `0` for „no header".
 *
 * This is the field a frozen feed shows up in: our server keeps answering 200
 * and bumps `Snapshot.ztmUpdatedAt` on every poll, so only the header stamp
 * standing still says the data did (Decision 37, §9.3 row 17). A consumer that
 * gets `null` falls back to `ztmUpdatedAt` — our refresh is a worse answer than
 * ZTM's, and a better one than none.
 *
 * Exported for `src/data/data.test.ts` alone (§4.3).
 */
export function feedClock(feedTs: number, now: number): number | null {
  // `<= 0` is belt and braces, and deliberately so: the wire's „no header" is
  // `0`, which the day cap below rejects anyway, as it does any negative stamp.
  // Only `Number.isFinite` is load-bearing on its own, so nobody should go
  // hunting for the test that kills the rest of this line — there is none.
  // Saying „no header is not a clock" where it is read is worth the line.
  if (!Number.isFinite(feedTs) || feedTs <= 0) return null;
  const at = feedTs * 1000;
  if (at > now + CLOCK_SKEW_MS) return null;
  return now - at > IMPLAUSIBLE_FEED_AGE_MS ? null : at;
}

const STARTING: SourceHealth = { state: 'starting', error: null, updatedAt: 0 };

/**
 * Consecutive polls that never reached our server before we call the
 * connection lost. One miss is a blip — the same tolerance `src/data/vehicles.ts`
 * gives the vehicle poll, and the reason this rule is needed at all.
 */
const MISSES_FOR_OFFLINE = 2;

/** What successive health polls have left us holding for one source. */
export type HealthPolls = {
  /** The last reading our server actually gave us. */
  reading: SourceHealth;
  /** Polls since then that never reached it. */
  misses: number;
};

/**
 * What a health poll leaves behind.
 *
 * A poll that never reached our server says nothing about ZTM, so it changes
 * nothing about ZTM: the previous reading is held, and on a cold start that is
 * `'starting'`. It only counts a miss. This exists because of a lie the kreska
 * used to tell: `serverReachable` needs two failed vehicle polls before it will
 * call the connection lost — a deliberate tolerance for one dropped request —
 * and in that window a failed health poll arrived as „ZTM is down". So a total
 * outage announced „ZTM nie odpowiada" for a poll or two before settling on
 * „brak połączenia z serwerem", blaming ZTM for our own lost connection, which
 * is the shape of lie this theme exists to remove.
 *
 * An answer that arrives is taken whole, whatever it says, and clears the
 * misses. A 5xx, a body that will not parse and a missing entry are answers:
 * our server telling us it knows nothing about that source, which still reads
 * `down` (§9.3). Only „the request did not come back" is held.
 *
 * Exported for `src/data/data.test.ts` alone (§4.3).
 */
export function afterHealthPoll(before: HealthPolls, answer: HealthAnswer): HealthPolls {
  if (answer.reached) return { reading: answer.health, misses: 0 };
  return { reading: before.reading, misses: before.misses + 1 };
}

/**
 * The two facts a snapshot carries about our own server, from the two signals
 * that know anything about it. The connection is lost when either poll says so
 * — the vehicle poll after two misses of its own (`src/data/vehicles.ts`), the
 * health poll after `MISSES_FOR_OFFLINE` — and each signal's count restarts on
 * its own recovery, so one coming back does not excuse the other.
 *
 * This is the decision point of the bug `afterHealthPoll` exists for: holding
 * the reading is only half of it, and the other half is not calling ZTM down
 * because we cannot reach ourselves.
 *
 * Exported for `src/data/data.test.ts` alone (§4.3).
 */
export function settleServer(
  polls: HealthPolls,
  vehiclesOffline: boolean,
): { serverReachable: boolean; ztm: SourceHealth } {
  const serverReachable = !vehiclesOffline && polls.misses < MISSES_FOR_OFFLINE;
  return { serverReachable, ztm: ztmHealth(polls.reading, serverReachable) };
}

/** Everything `startData` has been told, as it stands between two polls. */
type Parts = {
  vehicles: Vehicle[];
  events: Event[];
  alerts: Alert[];
  health: Record<Voice, SourceHealth>;
  /** OUR server's last successful refresh. */
  ztmUpdatedAt: number;
  /** ZTM's own clock, in GTFS-RT seconds, straight off the wire. */
  ztmFeedTsS: number;
  serverReachable: boolean;
};

/**
 * Four sources that arrived at four different moments, as one picture. Pure, so
 * the wiring has a test: reaching it through `startData` means arming four
 * polls and leaving the runner holding handles, which is why the throw that
 * guards a second `startData` has no test either.
 *
 * `health` is copied because the caller keeps mutating its own.
 *
 * Exported for `src/data/data.test.ts` alone (§4.3).
 */
export function snapshotOf(
  parts: Parts,
  now: number,
  positions: ReturnType<typeof vehiclePositions>,
): Snapshot {
  const nowS = now / 1000;
  return {
    items: [
      ...parts.vehicles.map((v) => vehicleItem(v, nowS, positions.get(v.id))),
      ...parts.events.map((e) => eventItem(e, now)),
      ...parts.alerts.map((a) => alertItem(a, now)),
    ],
    health: { ...parts.health },
    ztmUpdatedAt: parts.ztmUpdatedAt,
    ztmFeedTs: feedClock(parts.ztmFeedTsS, now),
    serverReachable: parts.serverReachable,
  };
}

/**
 * What the vehicle feed's health is — the server's own reading (ADR-0007),
 * never a guess from how old the snapshot is. Those are two different facts and
 * the kreska has to tell them apart: „dane z 13:57" (§9.3 row 17) is a
 * healthy ZTM whose last answer is ageing, and „⊘ 13:40" (row 18) is a ZTM that
 * stopped answering. The snapshot's age is `Snapshot.ztmUpdatedAt`, and this
 * function cannot read it, which is the point of the signature.
 *
 * Exported for `src/data/data.test.ts` alone (§4.3).
 */
export function ztmHealth(server: SourceHealth, serverReachable: boolean): SourceHealth {
  // our own server not answering /api/vehicles outranks whatever /api/health
  // last managed to say
  if (!serverReachable) return { state: 'down', error: server.error, updatedAt: server.updatedAt };
  return server;
}

/**
 * A link we are willing to put in an `href`, or nothing at all.
 *
 * Every `url` on the wire comes from a page we scraped — poznan.pl, Aquanet,
 * the ZTM RSS — so it is somebody else's string, and the seam is the trust
 * boundary. Only an absolute `http:` or `https:` URL survives; a
 * `javascript:`, `data:`, `vbscript:` or `blob:` scheme, a protocol-relative
 * `//host`, anything unparsable and anything that is not a string leave the key
 * ABSENT, so a card renders no link rather than a dangerous one. The value
 * returned is the parsed form, not the raw string.
 *
 * Exported for `src/data/data.test.ts` alone (§4.3).
 */
export function safeUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  let parsed: URL;
  try {
    // no base on purpose: a relative or protocol-relative reference from a
    // scraped page has no business becoming a link on our origin
    parsed = new URL(value);
  } catch {
    return undefined;
  }
  return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : undefined;
}

const place = (lat: number | null, lon: number | null) => (lat == null || lon == null ? null : { lat, lon });

/** A vehicle of one of my lines, as the axis and the circle see it. Exported for the tests that hold §4.4's grades. */
export function vehicleItem(v: Vehicle, nowS: number, at: [number, number] | undefined): Item {
  // an implausible number is not a fact about a tram, so it is not treated as
  // one: it reads unknown, sorts with the unknowns and is counted in nothing
  const delay = v.delay != null && Math.abs(v.delay) > IMPLAUSIBLE_DELAY_S ? null : v.delay;
  const delayMin = delayMinutes(delay);
  const late = delayLabel(delay);
  const uncertain = nowS - v.ts > STALE_FIX_S;
  return {
    // The feed's own id, stable across polls. A line has many vehicles on the
    // road at once, so `v16` would name a dozen of them at rush hour (§4.4).
    id: `v${v.id}`,
    kind: 'vehicle',
    voice: 'ztm',
    at: v.ts * 1000,
    title: v.headsign ? `${v.line} → ${v.headsign}` : v.line,
    glyph: v.type === 'tram' ? '▢' : '◯',
    sub: {
      // A feed that said nothing about the schedule has not said „on time":
      // `ink` is the tone for a fact we do not have (§7.2).
      a: delayMin == null ? '' : late || '✓ 0 min',
      // `delayLabel` is empty below its two-minute threshold, so a vehicle a
      // minute off still reads „✓ 0 min"; only a feed that said nothing is `ink`.
      tone: delayMin == null ? 'ink' : late ? 'brick' : 'ok',
      b: `◦ ${Math.max(0, Math.round(nowS - v.ts))} s`,
    },
    // Where `src/data/vehicles.ts` says it is, which since #176 is its latest fix.
    // The fallback is for a vehicle the seam has no track for at all; it is the
    // same coordinates, not a second opinion.
    position: at ? { lat: at[1], lon: at[0] } : { lat: v.lat, lon: v.lon },
    uncertain,
    // a fix we cannot trust reads quieter than one we can, and a tram running
    // ahead of schedule is the least urgent thing on the axis
    weight: uncertain ? WEIGHT.stale : delayMin != null && delayMin <= -2 ? WEIGHT.early : WEIGHT.full,
    mode: v.type,
    lines: [v.line],
    delayMin,
    heading: v.bearing,
    ...(v.nextStop ? { nextStop: v.nextStop } : {}),
  };
}

/** One entry of poznan.pl's calendar. Exported for the tests that hold §4.4's grades. */
export function eventItem(e: Event, now: number): Item {
  const at = new Date(e.startsAt).getTime();
  const started = at <= now;
  const url = safeUrl(e.url);
  return {
    id: `e${e.id}`,
    kind: 'event',
    voice: 'pl',
    at,
    title: e.title,
    glyph: '◇',
    sub: { a: started ? 'Zaczęło się' : 'Zaczyna się', tone: 'ink', b: e.place },
    position: place(e.lat, e.lon),
    uncertain: false,
    ...(url ? { url } : {}),
    // an event already under way is still worth reading, just less loudly
    weight: started ? WEIGHT.started : WEIGHT.full,
  };
}

/** A water fault from Aquanet, or a detour notice from ZTM. Exported for the tests that hold §4.4's grades. */
export function alertItem(a: Alert, now: number): Item {
  const at = new Date(a.publishedAt).getTime();
  const ztm = a.kind === 'ztm';
  const kind: ItemKind = ztm ? 'notice' : 'alert';
  const url = safeUrl(a.url);
  return {
    id: `${ztm ? 'n' : 'a'}${a.id}`,
    kind,
    voice: ztm ? 'ztmk' : 'aq',
    at,
    title: a.title,
    glyph: '○',
    sub: { a: 'Zgłoszono', tone: 'ink', b: '' },
    position: place(a.lat, a.lon),
    uncertain: false,
    ...(url ? { url } : {}),
    weight: now - at > OLD_MS ? WEIGHT.old : WEIGHT.full,
    ...(a.lines?.length ? { lines: a.lines } : {}),
  };
}

const subscribers = new Set<(s: Snapshot) => void>();
let last: Snapshot | null = null;
let started = false;

/**
 * Every module that draws from the snapshot subscribes here. `startData`
 * publishes to all subscribers; a late subscriber is called at once with the
 * last snapshot, if there is one — mounting after the first poll must not mean
 * waiting ten seconds for a screen.
 */
export function onSnapshot(fn: (s: Snapshot) => void): () => void {
  subscribers.add(fn);
  if (last) tell(fn, last);
  return () => {
    subscribers.delete(fn);
  };
}

/**
 * One subscriber's mistake is its own. `src/data/feed.ts` takes the same stance:
 * a renderer that throws is a bug in us, not the source being down, and it may
 * not stop the polling or the modules beside it.
 */
function tell(fn: (s: Snapshot) => void, snapshot: Snapshot) {
  try {
    fn(snapshot);
  } catch (e) {
    console.warn('data: a subscriber threw on a snapshot', e);
  }
}

/**
 * Hands one snapshot to everybody and remembers it for whoever subscribes next.
 *
 * Exported for `src/data/data.test.ts` alone (§4.3), because this is the whole of
 * `onSnapshot`'s contract and reaching it through `startData` would mean
 * standing up four endpoints and four polling intervals to assert a fan-out.
 */
export function publish(snapshot: Snapshot): void {
  last = snapshot;
  // A copy, because a subscriber may subscribe or dispose from inside its own
  // callback: iterating the live Set would visit a newcomer that `onSnapshot`
  // has already handed `last`, delivering the same snapshot twice, and `has`
  // keeps a disposal mid-publish honest.
  const listening = [...subscribers];
  for (const fn of listening) {
    if (subscribers.has(fn)) tell(fn, snapshot);
  }
}

/**
 * Starts every poll this app has and publishes the whole picture each time any
 * of them answers. One snapshot, so a caller never has to reconcile four feeds
 * that arrived at four different moments.
 *
 * The composition root calls this once; everything else uses `onSnapshot`.
 * Calling it twice would double every poll against sources we are polite to,
 * so it throws rather than quietly doing that.
 */
export function startData(onChange: (s: Snapshot) => void): void {
  if (started) {
    throw new Error('startData runs once: everything else subscribes with onSnapshot()');
  }
  started = true;
  let vehicles: Vehicle[] = [];
  let events: Event[] = [];
  let alerts: Alert[] = [];
  const health: Record<Voice, SourceHealth> = {
    ztm: STARTING,
    ztmk: STARTING,
    pl: STARTING,
    aq: STARTING,
  };
  let ztmUpdatedAt = 0;
  let ztmFeedTsS = 0;
  let serverReachable = true;

  function announce() {
    const now = Date.now();
    const snapshot = snapshotOf(
      { vehicles, events, alerts, health, ztmUpdatedAt, ztmFeedTsS, serverReachable },
      now,
      vehiclePositions(),
    );
    tell(onChange, snapshot);
    publish(snapshot);
  }

  /** `sourceHealth` answers `down` rather than throwing, so a rejection here
   * would be a bug in us and is reported as one. */
  async function splitVoices() {
    try {
      const [aq, ztmk] = await Promise.all([sourceHealth('alerts'), sourceHealth('notices')]);
      health.aq = aq;
      health.ztmk = ztmk;
      announce();
    } catch (e) {
      console.warn('data: the two utrudnienia voices could not be told apart', e);
    }
  }

  let ztmSaid: HealthPolls = { reading: STARTING, misses: 0 };
  let vehiclesOffline = false;

  /** The one place `serverReachable` and `health.ztm` are settled, so the two
   * callers below cannot drift apart on what a lost connection means. */
  function settleZtm() {
    const settled = settleServer(ztmSaid, vehiclesOffline);
    serverReachable = settled.serverReachable;
    health.ztm = settled.ztm;
  }

  async function pollZtmHealth() {
    let answer: HealthAnswer;
    try {
      answer = await healthOf('ztm');
    } catch (e) {
      // healthOf answers `reached: false` rather than throwing, so this is a bug in us
      console.warn('data: the ZTM health reading could not be taken', e);
      return;
    }
    ztmSaid = afterHealthPoll(ztmSaid, answer);
    settleZtm();
    announce();
  }

  const healthTick = () => {
    void pollZtmHealth();
  };
  healthTick();
  setInterval(healthTick, HEALTH_POLL_MS);

  startVehicles((snap, vehicleHealth) => {
    vehicles = snap.vehicles;
    ztmUpdatedAt = snap.updatedAt;
    ztmFeedTsS = snap.feedTs;
    vehiclesOffline = vehicleHealth.offline;
    settleZtm();
    announce();
  });

  startFeed<Event>('events', (feed) => {
    events = feed.items;
    health.pl = {
      state: feed.loading ? 'starting' : feed.error ? 'down' : 'ok',
      error: feed.error,
      updatedAt: feed.lastOk,
    };
    announce();
  });

  // `/api/alerts` carries two voices: Aquanet's water faults and ZTM's detour
  // notices. One endpoint, but the server reports them as two sources, and they
  // fail separately — so each voice gets its own health. Silencing one may never
  // silence the other's complaint, and an Aquanet outage may not suppress fresh
  // ZTM detours (§7.2, D-11, §9.3 rows 21, 38 and 39).
  startFeed<Alert>('alerts', (feed) => {
    alerts = feed.items;
    void splitVoices();
    announce();
  });
}
